import React from "react";
import { motion } from "framer-motion";
import { useT } from "../lib/i18n";

export const Section = ({ children, className = "", id }) => (
    <section id={id} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 ${className}`}>
        {children}
    </section>
);

export const SectionTitle = ({ kicker, title, accent }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.5 }}
        className="mb-10"
    >
        {kicker && <div className="label-mini mb-3">{kicker}</div>}
        <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none tracking-tight">
            {title} {accent && <span className="text-[#7FD93C]">{accent}</span>}
        </h2>
    </motion.div>
);

export const StatusBadge = ({ status }) => {
    const { t } = useT();
    if (status === "live") {
        return (
            <span className="inline-flex items-center gap-2 px-3 py-1 angle-tag bg-black border border-[#7FD93C] text-[#7FD93C] text-[11px] tracking-[0.3em] font-bold uppercase">
                <span className="w-2 h-2 rounded-full bg-[#7FD93C] live-pulse" />
                {t("status.live")}
            </span>
        );
    }
    if (status === "final") {
        return (
            <span className="inline-flex items-center gap-2 px-3 py-1 angle-tag bg-white/5 border border-white/15 text-white/80 text-[11px] tracking-[0.3em] font-bold uppercase">
                {t("status.final")}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-2 px-3 py-1 angle-tag border border-[#4FE0D5]/30 text-[#4FE0D5] text-[11px] tracking-[0.3em] font-bold uppercase">
            {t("status.scheduled")}
        </span>
    );
};

export const Reveal = ({ children, delay = 0, className = "" }) => (
    <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.5, delay }}
        className={className}
    >
        {children}
    </motion.div>
);

export const TeamLogo = ({ team, size = 36 }) => {
    // Prefer the full base64 data URL when available (admin pages); fall back to the
    // lightweight binary endpoint which is cacheable by the browser (public pages
    // load /api/teams?light=true and only get has_logo: bool).
    if (team?.logo_base64) {
        return <img src={team.logo_base64} alt={team.name} className="object-contain" style={{ width: size, height: size }} />;
    }
    if (team?.has_logo && team?.id) {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        return <img src={`${base}/api/teams/${team.id}/logo`} alt={team.name} className="object-contain" style={{ width: size, height: size }} loading="lazy" />;
    }
    const color = team?.color || "#7FD93C";
    const initials = (team?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
    return (
        <div
            className="flex items-center justify-center font-display text-lg tracking-wider angle-tag"
            style={{
                width: size,
                height: size,
                background: `linear-gradient(135deg, ${color}33, ${color}11)`,
                border: `1px solid ${color}55`,
                color,
            }}
        >
            {initials}
        </div>
    );
};
